// React / Next
import { useRef, useCallback, useMemo } from 'react';

// Misc
import { useAnimate, AnimationPlaybackControls } from 'motion/react';
import { cn } from '@/utils/cn';

// Types
type StaggerFrom = 'first' | 'last' | 'center' | number;

type AnimationOptions = {
  type?: 'spring' | 'tween';
  duration?: number;
  bounce?: number;
};

type LetterSwapPingPongProps = {
  /** The text to be displayed and animated */
  label: string;
  /** Direction of the animation (true: bottom to top, false: top to bottom) */
  reverse?: boolean;
  /** Animation configuration for each letter */
  transition?: AnimationOptions;
  /** Delay between each letter's animation start */
  staggerDuration?: number;
  /** Starting point of the stagger effect */
  staggerFrom?: StaggerFrom;
  /** Additional CSS classes for styling */
  className?: string;
  /** Callback function for click events */
  onClick?: () => void;
};

export const LetterSwapPingPong = ({
  label,
  reverse = true,
  transition = { type: 'spring', duration: 0.7 },
  staggerDuration = 0.03,
  staggerFrom = 'first',
  className,
  onClick,
}: LetterSwapPingPongProps) => {
  const [scope, animate] = useAnimate();
  const animationRef = useRef<AnimationPlaybackControls[]>([]);
  const isAnimatingRef = useRef(false);

  const letters = useMemo(() => label.split(''), [label]);

  const getStaggerDelay = useCallback(
    (index: number, totalLength: number) => {
      let startIndex: number;

      if (staggerFrom === 'first') {
        startIndex = 0;
      } else if (staggerFrom === 'last') {
        startIndex = totalLength - 1;
      } else if (staggerFrom === 'center') {
        startIndex = Math.floor(totalLength / 2);
      } else {
        startIndex = staggerFrom;
      }

      return Math.abs(index - startIndex) * staggerDuration;
    },
    [staggerFrom, staggerDuration]
  );

  const handleHoverStart = useCallback(async () => {
    if (isAnimatingRef.current) {
      animationRef.current.forEach((anim) => anim.stop());
    }
    isAnimatingRef.current = true;

    const originalLetters = scope.current?.querySelectorAll('.letter-original');
    const swapLetters = scope.current?.querySelectorAll('.letter-swap');

    if (!originalLetters || !swapLetters) return;

    const animations: AnimationPlaybackControls[] = [];

    originalLetters.forEach((letter: Element, i: number) => {
      const delay = getStaggerDelay(i, letters.length);
      const anim = animate(
        letter,
        { y: reverse ? '-100%' : '100%' },
        { ...transition, delay }
      );
      animations.push(anim);
    });

    swapLetters.forEach((letter: Element, i: number) => {
      const delay = getStaggerDelay(i, letters.length);
      const anim = animate(
        letter,
        { y: reverse ? '-100%' : '100%' },
        { ...transition, delay }
      );
      animations.push(anim);
    });

    animationRef.current = animations;

    await Promise.all(animations.map((anim) => anim.finished));
    isAnimatingRef.current = false;
  }, [animate, getStaggerDelay, letters.length, reverse, scope, transition]);

  const handleHoverEnd = useCallback(async () => {
    if (isAnimatingRef.current) {
      animationRef.current.forEach((anim) => anim.stop());
    }
    isAnimatingRef.current = true;

    const originalLetters = scope.current?.querySelectorAll('.letter-original');
    const swapLetters = scope.current?.querySelectorAll('.letter-swap');

    if (!originalLetters || !swapLetters) return;

    const animations: AnimationPlaybackControls[] = [];

    originalLetters.forEach((letter: Element, i: number) => {
      const delay = getStaggerDelay(i, letters.length);
      const anim = animate(letter, { y: '0%' }, { ...transition, delay });
      animations.push(anim);
    });

    swapLetters.forEach((letter: Element, i: number) => {
      const delay = getStaggerDelay(i, letters.length);
      const anim = animate(letter, { y: '0%' }, { ...transition, delay });
      animations.push(anim);
    });

    animationRef.current = animations;

    await Promise.all(animations.map((anim) => anim.finished));
    isAnimatingRef.current = false;
  }, [animate, getStaggerDelay, letters.length, scope, transition]);

  return (
    <span
      ref={scope}
      className={cn('relative inline-flex overflow-hidden', className)}
      onMouseEnter={handleHoverStart}
      onMouseLeave={handleHoverEnd}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick?.();
        }
      }}
    >
      {/* Original letters */}
      <span className="sr-only">{label}</span>
      <span aria-hidden="true" className="inline-flex">
        {letters.map((letter, index) => (
          <span
            key={`original-${index}`}
            className="letter-original inline-block"
            style={{ whiteSpace: letter === ' ' ? 'pre' : 'normal' }}
          >
            {letter}
          </span>
        ))}
      </span>
      {/* Swap letters */}
      <span
        aria-hidden="true"
        className="absolute left-0 top-0 inline-flex"
        style={{ transform: reverse ? 'translateY(100%)' : 'translateY(-100%)' }}
      >
        {letters.map((letter, index) => (
          <span
            key={`swap-${index}`}
            className="letter-swap inline-block"
            style={{ whiteSpace: letter === ' ' ? 'pre' : 'normal' }}
          >
            {letter}
          </span>
        ))}
      </span>
    </span>
  );
};

